import { useTranslation } from 'react-i18next'
import { OdinInput } from '@/components/ui/odin/form/odin-input'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { required } from 'utils/validations'
import { OdinButton } from '@/components/ui/odin/odin-button'
import { OdinInputPassword } from '@/components/ui/odin/form/odin-input-password'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui/Modal'
import { useModal } from 'providers/ModalProvider'
import { useAuthFlow } from 'hooks/useAuthFlow'
import { RegisterModal } from 'components/modals/RegisterModal'
import { ResetPasswordModal } from 'components/modals/ResetPasswordModal'

type LoginModalProps = {
  onSuccess?: () => void
  onResetPassword?: () => void
  onRegister?: () => void
}

const schema = z.object({
  email: required,
  password: required,
})

type LoginFormValues = z.infer<typeof schema>

export const LoginModal = ({ onSuccess, onResetPassword, onRegister }: LoginModalProps) => {
  const { t } = useTranslation()
  const { openModal, closeModal } = useModal()
  const { handleLogin, isLoading } = useAuthFlow()

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = async (values: LoginFormValues) => {
    try {
      await handleLogin(values, () => {
        onSuccess?.()
        closeModal()
      })
    } catch {
      // Error already handled in hook
    }
  }

  const onClickForgottenPassword = () => {
    if (onResetPassword) {
      return onResetPassword()
    }
    openModal(<ResetPasswordModal email={getValues('email')} />)
  }

  const onClickRegister = () => {
    if (onRegister) {
      return onRegister()
    }
    openModal(<RegisterModal />)
  }

  return (
    <Modal onClose={closeModal}>
      <ModalHeader title={t('login')} icon="right-to-bracket" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody className={'space-y-[12px]'}>
          <OdinInput label={t('email')} error={errors.email?.message} {...register('email')} />
          <OdinInputPassword label={t('password')} error={errors.password?.message} {...register('password')} />
          <div className={'cursor-pointer text-sm underline'} onClick={onClickForgottenPassword}>
            {t('forgottenPassword')}
          </div>
          <div className={'h-2'} />
          <div className={'cursor-pointer text-sm'} onClick={onClickRegister}>
            <span>{t('dontHaveAccount')}</span>
            <span className={'underline'}>{t('register')}</span>
          </div>
        </ModalBody>
        <ModalFooter>
          <OdinButton type={'submit'} className={'w-full'} loading={isLoading}>
            <span className={'text-sm'}>{t('login')}</span>
          </OdinButton>
        </ModalFooter>
      </form>
    </Modal>
  )
}
